import { Injectable } from "@angular/core";

import { FreightRate } from "./models/freight-rate.model";

// Online prices move, so a load is served again for an hour at most.
const onlineRateTtlMs = 60 * 60 * 1000;

interface OnlineRateLoad {
  rows: FreightRate[];
  loadedAt: number;
}

// The online rows each lane of a quote has loaded, by carrier type. Root-provided because the
// freight tab is destroyed while the quote is edited on another tab; a page reload forgets it.
@Injectable({ providedIn: "root" })
export class OnlineRateCacheService {
  private loadsByLane = new Map<string, Map<string, OnlineRateLoad>>();

  // Rows stored again under a type replace the earlier load.
  public store(
    quoteId: number,
    laneKey: string,
    carrierType: string,
    rows: FreightRate[],
  ): void {
    const key = this.cacheKey(quoteId, laneKey);
    const loads = this.loadsOf(key) ?? new Map<string, OnlineRateLoad>();
    loads.set(carrierType, { rows, loadedAt: Date.now() });
    this.loadsByLane.set(key, loads);
  }

  public loadedCarrierTypes(quoteId: number, laneKey: string): string[] {
    return [...(this.loadsOf(this.cacheKey(quoteId, laneKey))?.keys() ?? [])];
  }

  public rows(quoteId: number, laneKey: string): FreightRate[] {
    return [
      ...(this.loadsOf(this.cacheKey(quoteId, laneKey))?.values() ?? []),
    ].flatMap((load) => load.rows);
  }

  // The lane's loads still within the hour; older ones are dropped here.
  private loadsOf(key: string): Map<string, OnlineRateLoad> {
    const loads = this.loadsByLane.get(key);
    if (!loads) {
      return null;
    }

    const expiredBefore = Date.now() - onlineRateTtlMs;
    loads.forEach((load, carrierType) => {
      if (load.loadedAt <= expiredBefore) {
        loads.delete(carrierType);
      }
    });
    if (loads.size === 0) {
      this.loadsByLane.delete(key);

      return null;
    }

    return loads;
  }

  private cacheKey(quoteId: number, laneKey: string): string {
    return JSON.stringify([quoteId, laneKey]);
  }
}
